import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import {
    formatInrAmountByConfig,
    splitShikshaClientTypeAmountLabel,
} from '@core/helpers/format.helper'
import {
    SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT,
    SHIKSHA_DASHBOARD_API_ENDPOINTS,
    SHIKSHA_DASHBOARD_API_PARAMS,
    SHIKSHA_DASHBOARD_TEXT,
} from '@features/home/shiksha-components/constants/shiksha-dashboard.constants'
import type {
    IInrAmountFormatConfig,
    IShikshaClientTypeRevenue,
    IShikshaCustomerEstimatesResponse,
    IShikshaCustomerEstimatesRow,
} from '@core/models/shiksha-dashboard.model'
import { environment } from '@env/environment'
import type { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

@Injectable({ providedIn: 'root' })
export class ShikshaClientTypeRevenueService {
    private readonly httpClient = inject(HttpClient)
    private readonly inrAmountFormatConfig: IInrAmountFormatConfig = {
        currencyCode: SHIKSHA_DASHBOARD_TEXT.currencyCodeInr,
        croreThreshold: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.croreThreshold,
        lakhThreshold: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.lakhThreshold,
        croreDivisor: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.croreDivisor,
        lakhDivisor: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.lakhDivisor,
        croreUnit: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.croreUnit,
        lakhUnit: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.lakhUnit,
        locale: SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT.localeIndianEnglish,
    }

    getClientTypeRevenues(
        financialYear: number,
        userIds: string | null,
        scope: 'my' | 'team',
    ): Observable<readonly IShikshaClientTypeRevenue[]> {
        const endpoint = `${environment.apiBaseUrl}${SHIKSHA_DASHBOARD_API_ENDPOINTS.CUSTOMER_ESTIMATES_BY_FINANCIAL_YEAR}/${financialYear}`
        const params = this.buildUserParams(userIds, scope)
        return this.httpClient
            .get<IShikshaCustomerEstimatesResponse>(endpoint, { params })
            .pipe(map((response) => this.mapRowsToCardItems(this.extractRows(response))))
    }

    private buildUserParams(userIds: string | null, scope: 'my' | 'team'): HttpParams {
        let params = new HttpParams()
        if (scope === 'team') {
            params = params.set(SHIKSHA_DASHBOARD_API_PARAMS.SCOPE, scope)
        }

        const normalizedUserIds = userIds?.trim()
        if (!normalizedUserIds) {
            return params
        }

        if (normalizedUserIds.includes(',')) {
            return params.set(SHIKSHA_DASHBOARD_API_PARAMS.USER_IDS, normalizedUserIds)
        }

        return params.set(SHIKSHA_DASHBOARD_API_PARAMS.USER_ID, normalizedUserIds)
    }

    private extractRows(
        response:
            | IShikshaCustomerEstimatesResponse
            | readonly IShikshaCustomerEstimatesRow[]
            | null,
    ): readonly IShikshaCustomerEstimatesRow[] {
        if (!response) {
            return []
        }
        if (Array.isArray(response)) {
            return response
        }

        const rawData = (response as { data?: unknown }).data
        if (Array.isArray(rawData)) {
            return rawData as readonly IShikshaCustomerEstimatesRow[]
        }
        if (rawData && typeof rawData === 'object') {
            const dataObject = rawData as {
                rows?: unknown
                items?: unknown
                customer_estimates?: unknown
                client_type_revenue?: unknown
            }
            const candidates = [
                dataObject.rows,
                dataObject.items,
                dataObject.customer_estimates,
                dataObject.client_type_revenue,
            ]
            for (const candidate of candidates) {
                if (Array.isArray(candidate)) {
                    return candidate as readonly IShikshaCustomerEstimatesRow[]
                }
            }
        }
        return []
    }

    private mapRowsToCardItems(
        rows: readonly IShikshaCustomerEstimatesRow[] | undefined,
    ): readonly IShikshaClientTypeRevenue[] {
        if (!rows?.length) {
            return []
        }

        return rows
            .map((row) => {
                const label = (row.client_type_label ?? row.client_type ?? '').trim()
                const amount = row.total_payable ?? 0
                const amountLabel =
                    row.total_payable_label ??
                    row.total_estimated_amount_label ??
                    formatInrAmountByConfig(amount, this.inrAmountFormatConfig)
                const { primary } = splitShikshaClientTypeAmountLabel(amountLabel)
                const rawShare = row.payable_percent_of_total ?? 0
                const sharePercent = Number.isFinite(rawShare)
                    ? Math.max(0, Math.min(rawShare, 100))
                    : 0

                return {
                    label,
                    valuePrimary: primary,
                    sharePercent: Number(sharePercent.toFixed(1)),
                }
            })
            .filter((item) => item.label.length > 0)
    }
}
